import React, { useEffect, useState } from 'react';
import { FileTextOutlined, CheckCircleOutlined, LoadingOutlined, CloseCircleOutlined, DownOutlined } from '@ant-design/icons';
import CsvParser from '../components/CsvParser';
import { Steps, Button, Checkbox, Radio, Form, Card, Tree, Table, Col, Row, Divider, Tabs, Tag } from 'antd';
import AxiosOICStat from '../AxiosOICStat';
import { useMessage } from '../components/providers/MessageContext';
import { useSelector } from 'react-redux';
import { message } from 'antd/lib';
import PersistentErrorCard from './PersistentErrorCard';
import { getIndicatorName } from '../utils/indicatorName';

const { Step } = Steps;
const { TreeNode } = Tree;
const { TabPane } = Tabs;

export const CsvUploader = () => {
  const [step, setStep] = useState(0);
  const [csvData, setCsvData] = useState([]);
  const [csvDataToCheck, setCsvDataToCheck] = useState([]);
  const [isCsvUploaded, setIsCsvUploaded] = useState(false);
  const [columnsToCheck, setColumnsToCheck] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [scrollY, setScrollY] = useState(1000);
  const [options, setOptions] = useState({
    checkNullCells: false,
    checkNonNumberCells: true,
  });
  const [radioValue, setRadioValue] = useState('rowColumn');
  const [nullCheck, setNullCheck] = useState(false);
  const [nonNumberCheck, setNonNumberCheck] = useState(true);
  const [nullCells, setNullCells] = useState([]);
  const [nonNumberCells, setNonNumberCells] = useState([]);
  const [yearValues, setYearValues] = useState([]);
  const [uploadState, setUploadState] = useState('pending');
  const [uploadedIndsText, setUploadedIndsText] = useState("");
  const [errorReportingMethod, setErrorReportingMethod] = useState('rowColumn');
  const [groupedErrors, setGroupedErrors] = useState([]);
  const [fillWithNull, setFillWithNull] = useState(false);
  const [transformedData, setTransformedData] = useState([]);
  const { showMessage } = useMessage();
  const [checkedOnce, setCheckedOnce] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const countriesState = useSelector((state) => state.countries);
  const [warningMessage, setWarningMessage] = useState(null);
  const [persistentError, setPersistentError] = useState({ visible: false, title: '', message: '' });
  const indicatorsState = useSelector((state) => state.indicators);
  const [errorComponents, setErrorComponents] = useState({});

  const columns = [
    { title: 'Country Code', dataIndex: 'countryCode', key: 'countryCode' },
    { title: 'Indicator Code', dataIndex: 'indicatorCode', key: 'indicatorCode' },
    { title: 'Year Value', dataIndex: 'yearValue', key: 'yearValue' },
    { title: 'Cell No', dataIndex: 'cellNo', key: 'cellNo' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  const checkColumns = [
    { title: 'Error Type', dataIndex: 'name', key: 'name' },
    { title: 'Description', dataIndex: 'desc', key: 'desc' },
  ];

  function transformDataForTable(data) {
    const dataWithoutHeader = data.slice(1);
    const years = Array.from({ length: 52 }, (_, i) => 1970 + i);
    const transformedData = [];

    dataWithoutHeader.forEach(row => {
      const [indicatorCode, countryCode, ...values] = row;
      values.forEach((value, index) => {
        if (value) {
          transformedData.push({
            countryCode,
            indicatorCode,
            yearValue: years[index],
            cellNo: `${indicatorCode}-${countryCode}-${years[index]}`,
            value: value,
          });
        }
      });
    });

    return transformedData;
  }

  const findNullCells = (csvData) => {
    return csvData.slice(1).flatMap((row, rowIndex) => {
      const [indicatorCode, countryCode, ...rowCells] = row;
      return rowCells.map((cell, columnIndex) => {
        if (cell === null || cell === '') {
          const cellRef = `${columnIndexToLetter(columnIndex + 2)}-${rowIndex + 2}`;
          return {
            countryCode,
            indicatorCode,
            yearValue: yearValues[columnIndex],
            cellNo: cellRef,
            value: 'null',
          };
        }
        return null;
      }).filter(cell => cell !== null);
    });
  };

  const findNonNumberCells = (csvData) => {
    return csvData.slice(1).flatMap((row, rowIndex) => {
      const [indicatorCode, countryCode, ...rowCells] = row;
      return rowCells.map((cell, columnIndex) => {
        if (cell !== '' && cell !== null && cell !== undefined) {
          const numValue = parseFloat(cell);
          if (isNaN(numValue) || !isFinite(numValue)) {
            const cellRef = `${columnIndexToLetter(columnIndex + 2)}-${rowIndex + 2}`;
            return {
              countryCode,
              indicatorCode,
              yearValue: yearValues[columnIndex],
              cellNo: cellRef,
              value: cell,
            };
          }
        }
        return null;
      }).filter(cell => cell !== null);
    });
  };

  const columnIndexToLetter = (columnIndex) => {
    let columnLetter = '';
    let tempIndex = columnIndex;
    while (tempIndex >= 0) {
      columnLetter = String.fromCharCode('A'.charCodeAt(0) + (tempIndex % 26)) + columnLetter;
      tempIndex = Math.floor(tempIndex / 26) - 1;
    }
    return columnLetter;
  };

  const arrayToCSVString = (data) => {
    return data.map(row => row.join(',')).join('\n');
  };

  const handleReset = () => {
    setStep(0);
    resetStates(step, 0);
  };

  const downloadCSV = () => {
    const csvString = arrayToCSVString(csvData);
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "fixed_data.csv");
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadUploadedIndsText = () => {
    if (!uploadedIndsText) return;

    const currentDate = new Date();
    const dateString = currentDate.toISOString().split('T')[0];

    let formattedText = '';
    
    if (typeof uploadedIndsText === 'object') {
      formattedText += '=== Upload Summary ===\n\n';
      
      if (uploadedIndsText.summary) {
        formattedText += uploadedIndsText.summary + '\n\n';
      }
      
      if (uploadedIndsText.updated_indicators && uploadedIndsText.updated_indicators.length > 0) {
        formattedText += '=== Updated Indicators ===\n\n';
        uploadedIndsText.updated_indicators.forEach(indCode => {
          const indicator = indicatorsState.value[indCode];
          if (indicator) {
            formattedText += `Indicator Code: ${indCode}\n`;
            formattedText += `Indicator Name: ${getIndicatorName(indicator)}\n`;
            if (indicator.categories && indicator.categories.length > 0) {
              formattedText += `Categories: ${indicator.categories.join(', ')}\n`;
            }
            formattedText += '\n';
          } else {
            formattedText += `Indicator Code: ${indCode}\n\n`;
          }
        });
      }
      
      if (uploadedIndsText.details) {
        formattedText += '=== Additional Details ===\n\n';
        formattedText += JSON.stringify(uploadedIndsText.details, null, 2) + '\n';
      }
    } else {
      formattedText = uploadedIndsText;
    }

    const blob = new Blob([formattedText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `UploadedIndicators-${dateString}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

const prepareDataForUpload = (sourceData = csvData) => {
  if (!sourceData || sourceData.length === 0) {
    return [];
  }
  
  const yearColumns = sourceData[0].slice(2);

  return sourceData.slice(1).flatMap(row => {
    const indCode = row[0];
    const cCode = row[1];
    
    const isNumericCCode = !isNaN(cCode) && !isNaN(parseInt(cCode)) && cCode !== '';
    const cCodeValue = isNumericCCode ? parseInt(cCode) : null;
    const cNameValue = !isNumericCCode ? cCode : null;

    return yearColumns.map((year, index) => {
      const rawValue = row[index + 2];
      const isNumeric = !isNaN(rawValue) && !isNaN(parseFloat(rawValue)) && rawValue !== '';

      return {
        c_code: cCodeValue,
        c_name: cNameValue,
        ind_code: parseInt(indCode),
        d_year: parseInt(year),
        ...(isNumeric 
          ? { d_value: parseFloat(rawValue) }
          : { d_str_value: rawValue }
        )
      };
    });
  });
};

  const groupErrorsByCountry = (errors) => {
    const grouped = {};
    errors.forEach(error => {
      const { countryCode, indicatorCode, yearValue, value, cellNo } = error;
      if (!grouped[countryCode]) {
        grouped[countryCode] = {};
      }
      if (!grouped[countryCode][indicatorCode]) {
        grouped[countryCode][indicatorCode] = {};
      }
      if (!grouped[countryCode][indicatorCode][yearValue]) {
        grouped[countryCode][indicatorCode][yearValue] = [];
      }
      grouped[countryCode][indicatorCode][yearValue].push(`${value} (Cell: ${cellNo})`);
    });
    return grouped;
  };

  useEffect(() => {
    if (csvData.length > 0) {
      setYearValues(csvData[0].slice(2));
      setIsCsvUploaded(true);
    }
    const transformed = transformDataForTable(csvData);
    setTransformedData(transformed);
  }, [csvData]);

  const handleCsvData = (data) => {
    data.data = data.data.filter(row => row[0] !== '');
    setCsvData(data.data);
  };

  const handleCsvDataToCheck = async (data) => {
    const rows = data.data ? data.data : data;
    rows[0] = rows[0] || [];
    const filteredRows = rows.filter(row => row[0] !== '');
    const dataToUpload = prepareDataForUpload(filteredRows);
    console.log("handleCsvDataToCheck: ", dataToUpload);
    
    setIsChecking(true);
    setCheckedOnce(false);
    
    try {
      const response = await AxiosOICStat.post('/data/check-csv', dataToUpload);
      
      const errors = response.data.errors || [];
      const errorData = errors.map((error, index) => ({
        key: index,
        ...error
      }));
      
      setTableData(errorData);
      
      if (errors.length === 0) {
        setCheckedOnce(true);
        message.success("Data format is correct!");
        setTimeout(() => setCheckedOnce(false), 5000);
      } else {
        message.warning(response.data.summary || "There are some issues detected in the data. Please review them.");
      }
    } catch (error) {
      console.error('Check failed:', error);
      setPersistentError({ visible: true, title: 'CSV Check Failed', message: 'Failed to check CSV data. Please try again.' });
    } finally {
      setIsChecking(false);
    }
  };
  
  const resetStates = (currentStep, nextStep) => {
    while (currentStep >= nextStep) {
      resetStep(currentStep);
      currentStep--;
    }
  };

  const proceedToCheck = async () => {
    setIsChecking(true);
    
    try {
      const dataToUpload = prepareDataForUpload();
      const response = await AxiosOICStat.post('/data/check-csv', dataToUpload);
      
      const errors = response.data.errors || [];
      const errorData = errors.map((error, index) => ({
        key: index,
        ...error
      }));
      
      setTableData(errorData);
      setStep(2);
      
      if (errors.length === 0) {
        message.success("Data validation successful! You can proceed to upload.");
        setWarningMessage(null);
      } else {
        message.error(response.data.summary || "Validation failed. Please review the errors.");
        setWarningMessage(response.data.summary || "There are some issues detected in the data. Please review them.");
      }
    } catch (error) {
      console.error('Validation failed:', error);
      setPersistentError({ visible: true, title: 'CSV Validation Failed', message: 'Failed to validate CSV data. Please try again.' });
    } finally {
      setIsChecking(false);
    }
  }

  const letterToColumnIndex = (letter) => {
    let column = 0;
    let length = letter.length;
    for (let i = 0; i < length; i++) {
      column += (letter.charCodeAt(i) - 64) * Math.pow(26, length - i - 1);
    }
    return column;
  };

  const handleReportingMethodChange = e => {
    setErrorReportingMethod(e.target.value);
    setRadioValue(e.target.value);
  };

  const getTitleByDepth = (key, depth) => {
    switch (depth) {
      case 0:
        return `Country Code: ${key}`;
      case 1:
        return `Indicator Code: ${key}`;
      case 2:
        return `Year: ${key}`;
      default:
        return key;
    }
  };

  const proceedToNextStep = () => {
    if (tableData.length > 0) {
      message.error("Cannot proceed with upload. Please fix the validation errors first.");
      return;
    }
    
    if (fillWithNull) {
      applyFillErrorsWithNull();
    }
    performBatchUpload();
    setStep(3);
  };

  const applyFillErrorsWithNull = () => {
    let dataToFill = [];

    if (options.checkNonNumberCells) {
      dataToFill = [...nonNumberCells];
    }

    const updatedCsvData = csvData.map(row => [...row]);
    dataToFill.forEach((error) => {
      const [cellRef, rowIndex] = error.cellNo.split('-');
      const columnIndex = letterToColumnIndex(cellRef);
      updatedCsvData[parseInt(rowIndex) - 1][columnIndex - 1] = null;
    });

    setCsvData(updatedCsvData);
  };

  const performBatchUpload = async () => {
    setUploadState('uploading');
    const dataToUpload = prepareDataForUpload();
    console.log("performBatchUpload: ", dataToUpload);

    try {
      const response = await AxiosOICStat.post('/data/bulk_upsert', dataToUpload);
      console.log('Upload successful:', response.data);
      setUploadedIndsText(response.data);
      const summaryText = response.data.summary;
      if (response.data.is_success == 1){
        setUploadState('success');
        showMessage({ type: 'success', content: 'CSV Upload is successful', duration: 5 });
      }
      else{
        setUploadState('error');
        showMessage({ type: 'error', content: 'CSV Upload failed' + summaryText, duration: 5 });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      if (error.message.includes('Network Error') || error.code === 'ECONNREFUSED') {
        showMessage({
          type: 'error',
          content: (
            <div style={{ color: 'red', padding: '10px', fontFamily: 'Arial' }}>
              <p style={{ fontSize: '16px', fontWeight: 'bold' }}>Upload failed: Unable to connect to the server.</p>
              <p>Please check your internet connection or contact support if the problem persists.</p>
            </div>
          ),
          duration: 5
        });
      } else {
        showMessage({
          type: 'error',
          content: (
            <div style={{ color: 'red', fontFamily: 'Arial', padding: '10px' }}>
              <p style={{ fontWeight: 'bold' }}>CSV Upload failed due to an unexpected issue.</p>
              <p>Please contact the administrator.</p>
              <p><strong>Error Code:</strong> {error.code}</p>
              <p><strong>Message:</strong> {error.message}</p>
            </div>
          ),
          duration: 5
        });
      }
      setUploadState('error');
    }
  };

  const prepareDataSource = [...nonNumberCells, ...nullCells].map((error, index) => ({
    key: index,
    ...error,
  }));

  const renderTreeNodes = (data, path = '') => {
    return Object.keys(data).map(key => {
      const currentPath = path ? `${path}-${key}` : key;

      if (typeof data[key] === 'object') {
        return (
          <TreeNode title={getTitleByDepth(key, currentPath.split('-').length - 1)} key={currentPath}>
            {renderTreeNodes(data[key], currentPath)}
          </TreeNode>
        );
      }

      return <TreeNode title={data[key]} key={currentPath} />;
    });
  };

  const resetStep = (step) => {
    if (step === 2) {
      setUploadState('pending');
      setUploadedIndsText('');
    }
    if (step === 1) {
      setFillWithNull(false);
      setGroupedErrors([]);
      setNullCells([]);
      setNonNumberCells([]);
    }
    if (step === 0) {
      setCsvData([]);
      setIsCsvUploaded(false);
    }
  }

  const handleOptionsChange = (e) => {
    setOptions({ ...options, [e.target.name]: e.target.checked });
  };

  return (
    <div>
      <div>
        <PersistentErrorCard
          visible={persistentError.visible}
          title={persistentError.title}
          message={persistentError.message}
          onClose={() => setPersistentError({ visible: false, title: '', message: '' })}
        />
        <h1 style={{ textAlign: 'center' }}>
          <FileTextOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
          CSV Uploader
        </h1>

        <Steps current={step} style={{ marginBottom: '30px' }}>
          <Step title="Upload CSV" style={{ cursor: step > 0 ? 'pointer' : 'default' }} onClick={
            () => {
              if (step > 0) {
                resetStates(step, 0);
                setStep(0);
              }
            }
          } />
          
          <Step title="Validate Data" style={{ cursor: step > 2 ? 'pointer' : 'default' }} onClick={
            () => { 
              if (step > 2) {
                resetStates(step, 2);
                setStep(2);
              }
            }
          } />
          <Step title="Upload Completed" />
        </Steps>

            {step === 0 && (
              <>
                <CsvParser onCsvData={handleCsvData} />
                {isCsvUploaded && (
                  <Button
                    type="primary"
                    onClick={proceedToCheck}
                    loading={isChecking}
                    style={{ marginTop: '16px', display: 'block', marginLeft: 'auto', marginRight: 'auto', width: '200px' }}
                    disabled={csvData.length === 0 || isChecking}
                  >
                    Proceed to Next Step
                  </Button>
                )}
              </>
            )}

            {step === 1 && (
              <Card style={{ padding: '20px', textAlign: 'center' }}>
                <Row>
                  <Col span={11}>
                    <header style={{ fontWeight: 'bold', textDecoration: 'underline', textUnderlineOffset: '2px', fontSize: '16px' }}>Check Options</header>
                    <Row gutter={16} style={{ margin: '2%' }}>
                      <Col span={8}></Col>
                      <Checkbox name="checkNonNumberCells" onChange={(e) => {
                        setNonNumberCheck(!nonNumberCheck);
                        return handleOptionsChange(e);
                      }}
                        defaultChecked={nonNumberCheck}>
                        Check for non-number cells
                      </Checkbox>
                    </Row>
                    <Row gutter={16} style={{ margin: '2%' }}>
                      <Col span={8}></Col>
                      <Checkbox
                        name="checkNullCells"
                        onChange={(e) => {
                          setNullCheck(!nullCheck);
                          return handleOptionsChange(e)
                        }}
                        defaultChecked={nullCheck}
                      >
                        Check for null cells
                      </Checkbox>
                    </Row>
                  </Col>
                  <Col span={2}><Divider type="vertical" style={{ height: "100%" }} /></Col>
                  <Col span={11}>
                    <header style={{ fontWeight: 'bold', textDecoration: 'underline', textUnderlineOffset: '2px', fontSize: '16px' }}>Data Display Type</header>
                    <Radio.Group
                      onChange={handleReportingMethodChange}
                      defaultValue={radioValue}
                      style={{ width: '100%' }}
                    >
                      <Row gutter={16} style={{ margin: '2%' }}>
                        <Col span={4}></Col>
                        <Radio value="rowColumn">Give errors by row and column numbers (Table View)</Radio>
                      </Row>
                      <Row gutter={16} style={{ margin: '2%' }}>
                        <Col span={4}></Col>
                        <Radio value="groupByCountry">Give errors grouped by countries (Tree View)</Radio>
                      </Row>
                    </Radio.Group>
                  </Col>
                </Row>

                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                  <Button type="primary" onClick={proceedToCheck}>
                    Proceed to Next Step
                  </Button>
                </div>
              </Card>
            )
            }

            {
              step === 2 && (
                <Card>
                  <h2>Validation Results</h2>
                  {isChecking ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                      <LoadingOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                      <p style={{ marginTop: '20px', fontSize: '16px' }}>Validating CSV file...</p>
                    </div>
                  ) : (
                    <>
                      {warningMessage && (
                        <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fff2e8', border: '1px solid #ffbb96', borderRadius: '4px' }}>
                          <p style={{ margin: 0, color: '#d46b08' }}>{warningMessage}</p>
                        </div>
                      )}
                      {tableData.length > 0 ? (
                        <Table
                          columns={checkColumns}
                          dataSource={tableData}
                          pagination={{ pageSize: 20 }}
                          scroll={{ x: '100%' }}
                          bordered
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                          <Tag color="green" style={{ fontSize: '16px', padding: '10px 20px' }}>No errors found! File is ready for upload.</Tag>
                        </div>
                      )}
                      <div style={{ textAlign: 'center', marginTop: '16px' }}>
                        <Button type="primary" disabled={tableData.length !== 0} onClick={proceedToNextStep}>
                          Proceed to Upload
                        </Button>
                      </div>
                    </>
                  )}
                </Card>
              )
            }

            {
              step === 3 && (
                <Card style={{ padding: '20px', textAlign: 'center' }}>
                  <h2>
                    {
                      uploadState === 'uploading' && <LoadingOutlined style={{ color: 'blue', marginRight: '8px' }} />
                    }
                    {
                      uploadState === 'error' && <CloseCircleOutlined style={{ color: 'red', marginRight: '8px' }} />
                    }
                    {
                      uploadState === 'success' && <CheckCircleOutlined style={{ color: 'green', marginRight: '8px' }} />
                    }

                    {
                      uploadState === 'uploading' && 'Uploading data...'
                    }
                    {
                      uploadState === 'error' && 'Upload failed'
                    }
                    {
                      uploadState === 'success' && 'Upload successful'
                    }
                  </h2>
                  <Col style={{ textAlign: 'center' }}>
                    {
                      uploadedIndsText && <Col span={24}><Button type="primary" onClick={downloadUploadedIndsText} style={{ margin: '10px' }}>
                        Download Uploaded Indicators List
                      </Button></Col>
                    }
                    <Button type="primary" onClick={downloadCSV} style={{ margin: '10px' }}>
                      Download Fixed CSV
                    </Button>
                  </Col>
                </Card>
              )
            }

            {
              step > 0 && (
                <div style={{ textAlign: 'center', marginTop: '2%' }}>
                  <Button type="primary" onClick={handleReset}>
                    Reset and Upload Again
                  </Button>
                </div>
              )
            }
      </div>
    </div>
  );
};
